import React, { useState } from 'react';
import { FaStar, FaSpinner, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { FiMessageCircle, FiZap, FiTarget } from 'react-icons/fi';
import './ReviewFlow.css';
import { apiFetch } from '../../services/apiClient';

const RATING_LABELS = { 5: 'Excelente', 4: 'Muy bueno', 3: 'Bueno', 2: 'Regular', 1: 'Pobre' };

export default function ReviewFlow({ tradeId, counterpartyName, counterpartyId, onReviewSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState('');
  const [categories, setCategories] = useState({
    communication: 0,
    speed: 0,
    accuracy: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const charCount = text.length;
  const maxChars = 250;

  const handleCategoryChange = (category, value) => {
    setCategories(prev => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Por favor, califica al trader (1-5 estrellas)');
      return;
    }

    if (Object.values(categories).some(c => c === 0)) {
      setError('Por favor, califica todas las categorías');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await apiFetch(`/api/trades/${tradeId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          text: text.trim(),
          categories,
          toUserId: counterpartyId
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al enviar review');
      }

      setSuccess(true);
      setTimeout(() => {
        if (onReviewSubmitted) onReviewSubmitted();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Error al enviar el review');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="rf-success">
        <div className="rf-success-icon">
          <FaCheck size={48} />
        </div>
        <h3>¡Review enviado!</h3>
        <p>Tu calificación para <strong>@{counterpartyName}</strong> ha sido registrada.</p>
        <p className="rf-success-sub">Gracias por ayudar a la comunidad de traders.</p>
      </div>
    );
  }

  return (
    <div className="rf-container">
      <div className="rf-header">
        <h2>Califica tu experiencia</h2>
        <p>Ayuda a otros traders a tomar mejores decisiones</p>
        <p className="rf-counterparty">Reseña sobre <strong>@{counterpartyName}</strong></p>
      </div>

      {/* Overall Rating Stars */}
      <div className="rf-section">
        <label className="rf-label">Calificación General</label>
        <div className="rf-stars-main">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              className={`rf-star-btn ${star <= (hoverRating || rating) ? 'active' : ''}`}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(star)}
              title={`${star} estrella${star !== 1 ? 's' : ''}`}
            >
              <FaStar size={32} />
            </button>
          ))}
        </div>
        <div className="rf-rating-text">
          {rating > 0 && (
            <span className="rf-rating-display">
              <span className="rf-rating-stars">
                {Array.from({ length: rating }).map((_, i) => (
                  <FaStar key={i} size={13} />
                ))}
              </span>
              {RATING_LABELS[rating]}
            </span>
          )}
        </div>
      </div>

      {/* Category Ratings */}
      <div className="rf-section">
        <label className="rf-label">Califica por categoría</label>
        <div className="rf-categories">
          {[
            { key: 'communication', label: 'Comunicación', desc: '¿Respondió claro y a tiempo en el chat?', Icon: FiMessageCircle },
            { key: 'speed', label: 'Rapidez', desc: '¿Confirmó el pago y liberó la cripto rápido?', Icon: FiZap },
            { key: 'accuracy', label: 'Precisión', desc: '¿El monto, los datos y el método fueron correctos?', Icon: FiTarget }
          ].map(cat => (
            <div key={cat.key} className="rf-category">
              <div className="rf-category-header">
                <span className="rf-category-emoji"><cat.Icon /></span>
                <div className="rf-category-labels">
                  <label className="rf-category-label">{cat.label}</label>
                  <span className="rf-category-desc">{cat.desc}</span>
                </div>
              </div>
              <div className="rf-category-stars">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    className={`rf-category-star ${star <= categories[cat.key] ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(cat.key, star)}
                    title={`${star} estrella${star !== 1 ? 's' : ''}`}
                  >
                    <FaStar size={18} />
                  </button>
                ))}
              </div>
              <span className="rf-category-value fin-num">
                {categories[cat.key] > 0 ? `${categories[cat.key]}/5` : '-'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Review Text */}
      <div className="rf-section">
        <label className="rf-label">Comentario (opcional)</label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value.slice(0, maxChars))}
          placeholder="Comparte tu experiencia con este trader... (máx. 250 caracteres)"
          className="rf-textarea"
          rows={4}
        />
        <div className="rf-char-count fin-num">
          {charCount}/{maxChars} caracteres
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rf-error">
          <FaExclamationTriangle style={{ marginRight: '8px' }} />
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        className="rf-submit-btn"
        onClick={handleSubmit}
        disabled={loading || rating === 0}
      >
        {loading ? (
          <>
            <FaSpinner className="rf-spinner" size={16} />
            Enviando...
          </>
        ) : (
          <>
            <FaStar size={16} style={{ marginRight: '8px' }} />
            Enviar Review
          </>
        )}
      </button>

      <p className="rf-disclaimer">
        Tu review es público y ayuda a otros traders a decidir. Sé honesto y justo.
      </p>
    </div>
  );
}
